import { ReactNode, useId } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

export type RouterToggleButtonGroupProps<T extends boolean | string> = {
  'aria-label'?: string;
  getOptionLabel?: (option: T) => ReactNode;
  label?: string;
  options: T[];
  resetSearchParamKeys?: string[];
  searchParamKey: string;
};

export function RouterToggleButtonGroup<T extends string>({
  'aria-label': ariaLabel,
  getOptionLabel,
  label,
  options,
  resetSearchParamKeys,
  searchParamKey,
}: RouterToggleButtonGroupProps<T>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(searchParamKey) as null | T;
  const id = useId();

  return (
    <Grid alignItems='center' container spacing={1}>
      {label && (
        <Grid>
          <Typography display='inline' id={id} mr={1} variant='body2'>
            {label}:
          </Typography>
        </Grid>
      )}

      <Grid>
        <ToggleButtonGroup
          aria-label={ariaLabel}
          aria-labelledby={id}
          exclusive
          onChange={(_, val: string) =>
            setSearchParams(prev => {
              const next = new URLSearchParams(prev);
              if (val) next.set(searchParamKey, val);
              else next.delete(searchParamKey);
              resetSearchParamKeys?.forEach(key => next.delete(key));
              return next;
            })
          }
          size='small'
          value={value}
        >
          {options.map(value => (
            <ToggleButton key={value} size='small' value={value}>
              {getOptionLabel?.(value) ?? value}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Grid>
    </Grid>
  );
}

RouterToggleButtonGroup.displayName = 'RouterButton';
